import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  Calendar, 
  Settings, 
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Copy
} from 'lucide-react';
import { 
  weeklySlotApiService, 
  WeeklySlot, 
  WeeklySlotCreate, 
  WEEKDAYS, 
  APPOINTMENT_TYPES 
} from '../services/weeklySlotApi';

interface WeeklySlotManagerProps {
  onClose?: () => void;
}

const WeeklySlotManager: React.FC<WeeklySlotManagerProps> = ({ onClose }) => {
  const [slots, setSlots] = useState<WeeklySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([0, 1, 2, 3, 4])); // Lundi à Vendredi par défaut

  // États pour la génération de créneaux
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadSlots();
  }, []);

  const loadSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await weeklySlotApiService.getWeeklySlots();
      setSlots(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = (weekday: number) => {
    const newSlot: WeeklySlot = {
      weekday,
      start_time: '09:00',
      end_time: '10:00',
      appointment_type: 'both',
      max_bookings: 1,
      is_active: true,
      notes: ''
    };
    setSlots([...slots, newSlot]);
  };

  const handleUpdateSlot = (index: number, field: keyof WeeklySlot, value: any) => {
    const updatedSlots = [...slots];
    updatedSlots[index] = { ...updatedSlots[index], [field]: value };
    setSlots(updatedSlots);
  };

  const handleDeleteSlot = (index: number) => {
    const updatedSlots = slots.filter((_, i) => i !== index);
    setSlots(updatedSlots);
  };

  const handleSaveSlots = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Valider tous les créneaux
      const errors: string[] = [];
      const slotsToSave: WeeklySlotCreate[] = [];

      slots.forEach((slot, index) => {
        const slotErrors = weeklySlotApiService.validateSlot(slot);
        if (slotErrors.length > 0) {
          errors.push(`Créneau ${index + 1}: ${slotErrors.join(', ')}`);
        } else {
          slotsToSave.push({
            weekday: slot.weekday,
            start_time: slot.start_time,
            end_time: slot.end_time,
            appointment_type: slot.appointment_type,
            max_bookings: slot.max_bookings,
            is_active: slot.is_active,
            notes: slot.notes || ''
          });
        }
      });

      if (errors.length > 0) {
        setError(errors.join('\n'));
        return;
      }

      // Sauvegarder en masse
      const savedSlots = await weeklySlotApiService.bulkUpdateWeeklySlots(slotsToSave);
      setSlots(savedSlots);
      setSuccess('Créneaux sauvegardés avec succès !');
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSlots = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      const result = await weeklySlotApiService.generateSlotsForPeriod(
        generateStartDate, 
        generateEndDate
      );
      
      setSuccess(`${result.created_count} créneaux générés pour la période !`);
      setShowGenerateModal(false);
      setGenerateStartDate('');
      setGenerateEndDate('');
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyDay = (sourceWeekday: number, targetWeekday: number) => {
    const sourceSlots = slots.filter(slot => slot.weekday === sourceWeekday);
    const newSlots = sourceSlots.map(slot => ({
      ...slot,
      id: undefined, // Nouveau créneau
      weekday: targetWeekday
    }));
    
    // Supprimer les créneaux existants pour le jour cible
    const filteredSlots = slots.filter(slot => slot.weekday !== targetWeekday);
    setSlots([...filteredSlots, ...newSlots]);
  };

  const handleAddDefaultSlots = (weekday: number) => {
    const defaultSlots = weeklySlotApiService.generateDefaultSlotsForDay(weekday);
    const newSlots = defaultSlots.map(slot => ({ ...slot, id: undefined }));
    
    // Supprimer les créneaux existants pour ce jour
    const filteredSlots = slots.filter(slot => slot.weekday !== weekday);
    setSlots([...filteredSlots, ...newSlots]);
  };

  const handleFillFullWeekAgenda = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const fullWeekSlots = weeklySlotApiService.generateFullWeekAgenda();
      const savedSlots = await weeklySlotApiService.bulkUpdateWeeklySlots(fullWeekSlots);
      setSlots(savedSlots);
      setExpandedDays(new Set([0, 1, 2, 3, 4, 5, 6]));
      setSuccess('Agenda rempli : Lundi–Dimanche, 10h–22h (créneaux d’1h)');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleDayExpansion = (weekday: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(weekday)) {
      newExpanded.delete(weekday);
    } else {
      newExpanded.add(weekday);
    }
    setExpandedDays(newExpanded);
  };

  const groupedSlots = weeklySlotApiService.groupSlotsByWeekday(slots);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Chargement des créneaux...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Configuration des créneaux</h2>
            <p className="text-sm text-gray-600">Gérez les créneaux de rendez-vous pour chaque jour de la semaine</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleFillFullWeekAgenda}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50"
          >
            <Clock className="w-4 h-4" />
            <span>{saving ? 'Remplissage...' : 'Remplir Lun–Dim 10h–22h'}</span>
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Générer créneaux</span>
          </button>
          
          <button
            onClick={handleSaveSlots}
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </button>
        </div>
      </div>

      {/* Messages d'erreur et de succès */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-700 text-sm whitespace-pre-line">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div className="text-green-700 text-sm">{success}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Configuration par jour */}
      <div className="space-y-4">
        {WEEKDAYS.map((day) => {
          const daySlots = groupedSlots[day.value] || [];
          const isExpanded = expandedDays.has(day.value);

          return (
            <div key={day.value} className="border border-gray-200 rounded-lg">
              {/* Header du jour */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleDayExpansion(day.value)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <h3 className="text-lg font-semibold text-gray-900">{day.label}</h3>
                  <span className="text-sm text-gray-500">
                    {daySlots.length} créneau{daySlots.length !== 1 ? 'x' : ''}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {daySlots.length === 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDefaultSlots(day.value);
                      }}
                      className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                    >
                      Créneaux par défaut
                    </button>
                  )}
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddSlot(day.value);
                    }}
                    className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  
                  <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Créneaux du jour */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gray-200"
                  >
                    <div className="p-4 space-y-3">
                      {daySlots.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p>Aucun créneau configuré pour ce jour</p>
                          <button
                            onClick={() => handleAddSlot(day.value)}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Ajouter un créneau
                          </button>
                        </div>
                      ) : (
                        daySlots.map((slot, slotIndex) => {
                          const globalIndex = slots.findIndex(s => s === slot);
                          return (
                            <div key={slotIndex} className="grid grid-cols-12 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                              {/* Heure de début */}
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-600 mb-1">Début</label>
                                <input
                                  type="time"
                                  value={slot.start_time}
                                  onChange={(e) => handleUpdateSlot(globalIndex, 'start_time', e.target.value)}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                />
                              </div>

                              {/* Heure de fin */}
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-600 mb-1">Fin</label>
                                <input
                                  type="time"
                                  value={slot.end_time}
                                  onChange={(e) => handleUpdateSlot(globalIndex, 'end_time', e.target.value)}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                />
                              </div>

                              {/* Type de rendez-vous */}
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-600 mb-1">Type</label>
                                <select
                                  value={slot.appointment_type}
                                  onChange={(e) => handleUpdateSlot(globalIndex, 'appointment_type', e.target.value)}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                  {APPOINTMENT_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>
                                      {type.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Nombre max de réservations */}
                              <div className="col-span-1">
                                <label className="block text-xs text-gray-600 mb-1">Max</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={slot.max_bookings}
                                  onChange={(e) => handleUpdateSlot(globalIndex, 'max_bookings', parseInt(e.target.value))}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                />
                              </div>

                              {/* Notes */}
                              <div className="col-span-3">
                                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={slot.notes || ''}
                                  onChange={(e) => handleUpdateSlot(globalIndex, 'notes', e.target.value)}
                                  placeholder="Notes optionnelles"
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                                />
                              </div>

                              {/* Actif/Inactif */}
                              <div className="col-span-1 flex items-center justify-center">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={slot.is_active}
                                    onChange={(e) => handleUpdateSlot(globalIndex, 'is_active', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-1 text-xs text-gray-600">Actif</span>
                                </label>
                              </div>

                              {/* Actions */}
                              <div className="col-span-1 flex justify-end">
                                <button
                                  onClick={() => handleDeleteSlot(globalIndex)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Modal de génération de créneaux */}
      <AnimatePresence>
        {showGenerateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Générer les créneaux pour une période
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={generateStartDate}
                    onChange={(e) => setGenerateStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={generateEndDate}
                    onChange={(e) => setGenerateEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleGenerateSlots}
                  disabled={generating || !generateStartDate || !generateEndDate}
                  className="btn-primary flex items-center space-x-2"
                >
                  {generating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span>{generating ? 'Génération...' : 'Générer'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WeeklySlotManager;
